import * as electricityContractationActions from "../actions/electricityContractation";
import FormInstallation from "../components/FormInstallation";
import FormPerson from "../components/FormPerson";
import FormContract from "../components/FormContract";
import FormGasContract from "../components/electricity/FormGasContract";
import FormPayment from "../components/FormPayment";
import FormConfirmation from "../components/FormConfirmation";
import Finish from "../components/Finish";
import * as collectFormDataForAPI from "../utils/collectDataAPI";
import { default as leadsService } from "../services/leads";
import { StepsName } from "../types/steps";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { 
  IStepConfirmationFormData, 
  IStepPersonFormData,
  StepsFormData, 
} from "../types/stepsFormData";
import { isEqual } from "lodash";
import { useAppDispatch, useAppSelector } from "@/hooks";

interface IuseStepsProps {
  dualContractation: boolean
}

const useSteps = ({
  dualContractation
}: IuseStepsProps) => {
  const Settings = useSettingsContext();
  const dispatch = useAppDispatch();

  const elecContractation = useAppSelector(state => state.electricityContractation);

  //TODO: check dispatch(electricityContractationActions.resetLeadsErrors());

  const handleGoBack = () => {
    const prevStepIdx = elecContractation.currentStep.index - 1;
    dispatch(electricityContractationActions.resetLeadsErrors());
    dispatch(electricityContractationActions.setLeadCurrentStep(
      prevStepIdx, steps[prevStepIdx].name
    ));
  };

  const handleGoForward = () => {
    const followStepIdx = elecContractation.currentStep.index + 1;
    dispatch(
      electricityContractationActions.setLeadCurrentStep(
        followStepIdx, 
        steps[followStepIdx].name
      )
    );
  };

  // TODO: Pass number or string (step index or name)
  const handleGoToStep = (index: number) => {
    if (
      index <= Object.values(elecContractation.completedSteps).length && 
      elecContractation.latestStep !== steps.length-1 && 
      !elecContractation.submitting
    ) {
      dispatch(
        electricityContractationActions.setLeadCurrentStep(
          index, 
          steps[index].name
        ));
    }
  }

  let steps = [
    {
      name: 'installation',
      label: 'common:text.contractation_about_installation',
      content: <FormInstallation
          type="electricity"
          onSubmit={ async (values) => {

            const {leadsConsent, ...checkValues} = values;
            const oldLeadsConsent = elecContractation.leadsConsent;
            if (
              !isEqual(checkValues, elecContractation.steps.installation) ||
              leadsConsent != elecContractation.leadsConsent
            ) {
              
              // If leads send to the API
              if ( Settings.features.leads && 
                
                // Has given consent to store leads and receive communications
                (values.leadsConsent || 

                  // Edge case in which the user goes back one step 
                  // and unchecks leadConsent
                  (!values.leadsConsent && oldLeadsConsent)
                )
              ) {
                const elecInstallationFormData = collectFormDataForAPI.collectInstallationFormData(
                  values
                )

                try {
                  const {lead_id: leadId} = await leadsService.sendLeadStepData(
                    collectFormDataForAPI.collectInstallationFormData(values)
                  );

                  await dispatch(
                    electricityContractationActions.setLeadId(leadId)
                  );

                } catch {
                  await dispatch(electricityContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }

                if (elecContractation.steps.gasContract.includeGas) {
                  const gasInstallationFormData = elecInstallationFormData;

                  // This is needed to find the gas cups lead
                  gasInstallationFormData.FormConfirm.cups.name = elecContractation.steps.gasContract.cups;

                  // Without doing this it wont find the comerOrigin id in the ERP
                  // and fail the request.
                  gasInstallationFormData.FormConfirm.comerOrigin = elecContractation.steps.gasContract.comerOrigin;

                  await leadsService.sendGasLeadStepData(
                    gasInstallationFormData
                  );
                }
              }

              if (elecContractation.leadErrors.genericError) {
                await dispatch(electricityContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                electricityContractationActions.setInstallationData(values)
              );

            }

            await dispatch(
              electricityContractationActions.setLeadStepCompleted(
                elecContractation.currentStep.index
              )
            )

            await handleGoForward();

          }}
        />
    },
    {
      name: 'person',
      label: 'common:text.contractation_about_you',
      content: <FormPerson
          type="electricity"
          onSubmit={async (values: IStepPersonFormData) => {

            if (!isEqual(values, elecContractation.steps.person)) {
              
              // If leads send to the API
              if ( Settings.features.leads && elecContractation.leadsConsent ) {

                const elecPersonFormData = collectFormDataForAPI.collectPersonFormData(
                  values,
                  elecContractation.steps.installation.cups
                )
                
                try {
                  await leadsService.sendLeadStepData(
                    elecPersonFormData
                  );

                } catch {
                  await dispatch(electricityContractationActions.setLeadsErrors({
                    genericError: true
                  }));

                  return;  
                }

                if ( Settings.contractation?.shouldCheckDNI ) {
                  const { dni_valid } = await leadsService.checkLeadDNI(elecContractation.leadId)
                  if (!dni_valid) {
                    await dispatch(electricityContractationActions.setLeadsErrors({
                      dniInvalid: true
                    }));
                    
                    return;
                  }
                };

                if( !!Settings.contractation?.shouldCheckMorosity ) {
                  const { scoring } = await leadsService.checkLeadScore(elecContractation.leadId);
                  if (!scoring) {
                    await dispatch(electricityContractationActions.setLeadsErrors({
                      badScore: true
                    }));
    
                    return;
                  }
                }

                if (elecContractation.steps.gasContract.includeGas) {
                  const gasPersonFormData = elecPersonFormData;
                  gasPersonFormData.FormConfirm.cups.name = elecContractation.steps.gasContract.cups;
    
                  try {
                    await leadsService.sendGasLeadStepData(
                      gasPersonFormData
                    );
                  } catch {
                    await dispatch(electricityContractationActions.setLeadsErrors({
                      genericError: true
                    }));
                    return;
                  }
                }

              }

              if (elecContractation.leadErrors.genericError) {
                await dispatch(electricityContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                electricityContractationActions.setPersonData(values)
              );

            }

            // If there is no error
            await dispatch(
              electricityContractationActions.setLeadStepCompleted(
                elecContractation.currentStep.index
              )
            )

            await handleGoForward();

          }}

          onGoBack={handleGoBack}
        />
    },
    {
      name: 'contract',
      label: 'common:text.contractation_about_contract',
      content: <FormContract
          type="electricity"
          onSubmit={async (values) => {

            if (!isEqual(values, elecContractation.steps.contract)) {
              
              // If leads send to the API
              if ( Settings.features.leads && elecContractation.leadsConsent ) {
                try {
                  await leadsService.sendLeadStepData(
                    collectFormDataForAPI.collectContractFormData(
                      values,
                      elecContractation.steps.installation.cups
                    )
                  );

                } catch {
                  await dispatch(electricityContractationActions.setLeadsErrors({
                    genericError: true
                  }));

                  return;
                }
              }

              if (elecContractation.leadErrors.genericError) {
                await dispatch(electricityContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                electricityContractationActions.setContractData(values)
              );
            }

            await dispatch(
              electricityContractationActions.setLeadStepCompleted(
                elecContractation.currentStep.index
              )
            )
            
            await handleGoForward();
          
          }}
          onGoBack={handleGoBack}
        />
    },
    {
      name: 'gasContract',
      label: 'common:text.contractation_about_gas_contract',
      content: <FormGasContract
          type="electricity"
          onSubmit={async (values) => {

            if (!isEqual(values, elecContractation.steps.gasContract)) {

              if (values.includeGas) {

                // If leads send to the API
                if (
                  Settings.features.leads && elecContractation.leadsConsent ) {

                  // Send the first two steps data to a gas contractation
                  const gasInstallationFormData = collectFormDataForAPI.collectInstallationFormData({
                    ...elecContractation.steps.installation,
                    
                    // Must do this because otherwise the electricity comer id
                    // is not found in the gas comer available ids and fails 
                    cups: values.cups,
                    activeContract: values.activeContract,
                    comerOrigin: values.comerOrigin
                  });
                  
                  try {
                    const {lead_id: gasLeadId} = await leadsService.sendGasLeadStepData(
                      gasInstallationFormData
                    );
                    
                    await dispatch(
                      electricityContractationActions.setGasLeadId(gasLeadId)
                    );

                    await leadsService.sendGasLeadStepData(
                      collectFormDataForAPI.collectPersonFormData(
                        {
                          ...elecContractation.steps.person,
                          changeOwner: values.changeOwner
                        },
                        values.cups
                      )
                    );                 

                    // Send the actual gas step data
                    await leadsService.sendGasLeadStepData(
                      collectFormDataForAPI.collectGasContractFormData(
                        values,
                        elecContractation.leadId
                      )
                    );
                  
                  } catch {
                    await dispatch(electricityContractationActions.setLeadsErrors({
                      genericError: true
                    }));
                    return;
                  }

                }
              }

              if (elecContractation.leadErrors.genericError) {
                await dispatch(electricityContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                electricityContractationActions.setDualContractData(values)
              );
            }

            await dispatch(
              electricityContractationActions.setLeadStepCompleted(
                elecContractation.currentStep.index
              )
            )
          
            await handleGoForward();

          }}
          onGoBack={handleGoBack}
        />
    },
    {
      name: 'payment',
      label: 'common:text.contractation_about_payment',
      content: <FormPayment

          type="electricity"
          onSubmit={async (values) => {

            if (!isEqual(values, elecContractation.steps.payment)) {
              
              // If leads send to the API
              if ( Settings.features.leads && elecContractation.leadsConsent ) {
                
                try {
                  await leadsService.sendLeadStepData(
                    collectFormDataForAPI.collectPaymentFormData(
                      values,
                      elecContractation.steps.installation.cups
                    )
                  );

                  if (elecContractation.steps.gasContract.includeGas) {
                    // Send the actual gas step data
                    await leadsService.sendGasLeadStepData(
                      collectFormDataForAPI.collectPaymentFormData(
                        values,
                        elecContractation.steps.gasContract.cups
                      )
                    );
                  }

                } catch {
                  if (elecContractation.leadErrors.genericError) {
                    await dispatch(electricityContractationActions.setLeadsErrors({
                      genericError: true
                    }));
                  }
                }

              }

              if (elecContractation.leadErrors.genericError) {
                await dispatch(electricityContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                electricityContractationActions.setPaymentData(values)
              );

            }

            await dispatch(
              electricityContractationActions.setLeadStepCompleted(
                elecContractation.currentStep.index
              )
            )
            
            await handleGoForward();
          
          }}

          onGoBack={handleGoBack}
        />
    },
    {
      name: 'confirmation',
      label: 'common:text.contractation_confirmation',
      content: <FormConfirmation
          type="electricity"
          onSubmit={async (values: IStepConfirmationFormData) => {

            dispatch(electricityContractationActions.setConfirmationData(values));

            try {

              // If lead has not been created, must send all the steps info
              if (!(Settings.features.leads && elecContractation.leadsConsent)) {
                const { lead_id } = await leadsService.sendLeadStepData(
                  collectFormDataForAPI.collectElecFormData(
                    elecContractation.steps
                  )
                );

                if (elecContractation.steps.gasContract.includeGas) {
                  await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectDualGasFormData(
                      elecContractation.steps,
                      lead_id
                    )
                  );
                }
              }

              const elecRes = await leadsService.confirmContractation(
                collectFormDataForAPI.collectConfirmationFormData(
                  values,
                  elecContractation.steps.installation.cups
                )
              )
              
              if (elecContractation.steps.gasContract.includeGas) {
                await leadsService.confirmGasContractation(
                  collectFormDataForAPI.collectConfirmationFormData(
                    values,
                    elecContractation.steps.gasContract.cups
                  )
                );
              }

              if (
                ( !elecContractation.steps.gasContract.includeGas || 
                    ( elecContractation.steps.gasContract.includeGas )
                )
              ) {
                
                await dispatch(
                  electricityContractationActions.receiveConfirmNewContractResponse(
                    elecRes
                  )
                );

                await dispatch(
                  electricityContractationActions.setLeadStepCompleted(
                    elecContractation.currentStep.index
                  )
                )

                await handleGoForward();

              } else {

                dispatch(
                  electricityContractationActions.receiveConfirmNewContractResponse(
                    {
                      result: {
                        error: true,
                        render: {
                          format: 'markdown',
                          body: 'common:text.contractation_connectivity_error'
                        }
                      },
                      status: 'ERROR'
                    }
                  )
                );
              }
            
            } catch (err) {

              dispatch(
                electricityContractationActions.receiveConfirmNewContractResponse(
                  {
                    result: {
                      error: true,
                      render: {
                        format: 'markdown',
                        body: 'common:text.contractation_connectivity_error'
                      }
                    },
                    status: 'ERROR'
                  }
                )
              );
            }

          }}
          onGoBack={handleGoBack}
        />
    },
    {
      name: 'finish',
      label: 'common:text.contractation_confirmed',
      content: <Finish
          type="electricity"
          onGoBack={handleGoBack}
        />
    }
  ];

  const removeStep = (stepName: StepsName) => {
    steps = steps.filter(step => step.name !== stepName);
  }

  if (!dualContractation) {
    removeStep('gasContract');
  }

  if (
    Settings?.contractation?.businessHaltContractation && 
    elecContractation.steps.person.company
  ) {
    removeStep('contract');
    removeStep('gasContract');
    removeStep('payment');
  }

  return {
    steps,
    handleGoToStep,
    handleGoBack
  }

}

export default useSteps;